import {
	faChevronLeft,
	faChevronRight,
	faEllipsisH,
} from '@fortawesome/pro-solid-svg-icons';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

export interface TablePaginationControlsProps {
	currentPage: number;
	totalPages: number;
	onPageChange: (page: number) => void;
	disabled?: boolean;
	variant?: 'full' | 'compact';
	maxVisiblePages?: number;
	/** Optional prefix for test IDs */
	testIdPrefix?: string;
	/** "Showing X-Y of Z" summary info */
	startIndex?: number;
	endIndex?: number;
	totalItems?: number;
}

export function TablePaginationControls({
	currentPage,
	totalPages,
	onPageChange,
	disabled = false,
	variant = 'full',
	maxVisiblePages = 5,
	testIdPrefix,
	startIndex,
	endIndex,
	totalItems,
}: TablePaginationControlsProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();

	if (totalPages <= 1) {
		// Still show the summary text even on a single page
		if (totalItems != null && totalItems > 0) {
			return (
				<div className="flex items-center justify-between px-3 py-2">
					<Text className="text-xs" color="secondary">
						{t('Showing')} {t(totalItems.toLocaleString())} {t('of')}{' '}
						{t(totalItems.toLocaleString())} {t('results')}
					</Text>
				</div>
			);
		}
		return null;
	}

	const hasPrevPage = currentPage > 1;
	const hasNextPage = currentPage < totalPages;

	// Grouped digits: a server-paged report can run to six or seven figures,
	// where a bare "548632" is unreadable (PD-15826). The page buttons below
	// already group; this keeps the summary consistent with them.
	const summaryText =
		startIndex != null && endIndex != null && totalItems != null ? (
			<Text className="text-xs" color="secondary">
				{t('Showing')} {t(startIndex.toLocaleString())}
				{t('-')}
				{t(endIndex.toLocaleString())} {t('of')}{' '}
				{t(totalItems.toLocaleString())} {t('results')}
			</Text>
		) : null;

	// Compact variant - just prev/next with page info
	if (variant === 'compact') {
		return (
			<div className="flex items-center justify-between px-3 py-2">
				{summaryText}
				<div className="flex items-center">
					<button
						onClick={() => {
							onPageChange(currentPage - 1);
						}}
						disabled={!hasPrevPage || disabled}
						className={cn(
							'p-2 rounded-md border transition-colors',
							hasPrevPage && !disabled
								? cn(
										colors.classes.bgSecondary,
										colors.classes.borderDefault,
										colors.classes.bgPrimaryHover,
									)
								: cn(
										colors.classes.bgSecondary,
										colors.classes.borderDefault,
										'cursor-not-allowed',
									),
						)}
						type="button"
						data-testid={
							testIdPrefix != null
								? `${testIdPrefix}-pagination-prev`
								: undefined
						}
					>
						<Icon
							icon={faChevronLeft}
							size={12}
							color={
								hasPrevPage && !disabled
									? colors.values.primaryDark
									: colors.values.textSecondary
							}
						/>
					</button>

					<div className="px-3 py-1 min-w-[80px] text-center">
						<Text className="text-xs font-medium">
							{currentPage.toLocaleString()} {t('of')}{' '}
							{totalPages.toLocaleString()}
						</Text>
					</div>

					<button
						onClick={() => {
							onPageChange(currentPage + 1);
						}}
						disabled={!hasNextPage || disabled}
						className={cn(
							'p-2 rounded-md border transition-colors',
							hasNextPage && !disabled
								? cn(
										colors.classes.bgSecondary,
										colors.classes.borderDefault,
										colors.classes.bgPrimaryHover,
									)
								: cn(
										colors.classes.bgSecondary,
										colors.classes.borderDefault,
										'cursor-not-allowed',
									),
						)}
						type="button"
						data-testid={
							testIdPrefix != null
								? `${testIdPrefix}-pagination-next`
								: undefined
						}
					>
						<Icon
							icon={faChevronRight}
							size={12}
							color={
								hasNextPage && !disabled
									? colors.values.primaryDark
									: colors.values.textSecondary
							}
						/>
					</button>
				</div>
			</div>
		);
	}

	// Full variant - numbered page buttons
	const getPageNumbers = () => {
		const pages: Array<number | 'ellipsis'> = [];

		if (totalPages <= maxVisiblePages + 2) {
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i);
			}
		} else {
			pages.push(1);

			const halfVisible = Math.floor((maxVisiblePages - 2) / 2);
			let start = Math.max(2, currentPage - halfVisible);
			let end = Math.min(totalPages - 1, currentPage + halfVisible);

			if (currentPage <= halfVisible + 1) {
				end = maxVisiblePages - 1;
			} else if (currentPage >= totalPages - halfVisible) {
				start = totalPages - maxVisiblePages + 2;
			}

			if (start > 2) {
				pages.push('ellipsis');
			}

			for (let i = start; i <= end; i++) {
				pages.push(i);
			}

			if (end < totalPages - 1) {
				pages.push('ellipsis');
			}

			pages.push(totalPages);
		}

		return pages;
	};

	const pageNumbers = getPageNumbers();

	return (
		<div className="flex items-center justify-between px-3 py-2">
			{summaryText}
			<div className="flex items-center gap-1">
				{/* Previous button */}
				<button
					onClick={() => {
						onPageChange(currentPage - 1);
					}}
					disabled={!hasPrevPage || disabled}
					className={cn(
						'py-0.5 px-1.5 text-xs rounded-md border transition-colors',
						hasPrevPage && !disabled
							? cn(
									colors.classes.bgPrimary,
									colors.classes.borderDefault,
									colors.classes.bgPrimaryHover,
								)
							: cn(
									colors.classes.bgSecondary,
									colors.classes.borderDefault,
									'cursor-not-allowed',
								),
					)}
					type="button"
					data-testid={
						testIdPrefix != null ? `${testIdPrefix}-pagination-prev` : undefined
					}
				>
					<Icon
						icon={faChevronLeft}
						size={12}
						color={
							hasPrevPage && !disabled
								? colors.values.iconPrimary
								: colors.values.iconSecondary
						}
					/>
				</button>

				{/* Page numbers */}
				{pageNumbers.map((page, index) => {
					if (page === 'ellipsis') {
						return (
							<div key={`ellipsis-${index}`} className="px-1">
								<Icon
									icon={faEllipsisH}
									size={12}
									color={colors.values.textSecondary}
								/>
							</div>
						);
					}

					const isActive = page === currentPage;

					return (
						<button
							key={page}
							onClick={() => {
								onPageChange(page);
							}}
							disabled={isActive || disabled}
							className={cn(
								'min-w-[24px] py-0.5 px-1.5 text-xs rounded-md border transition-colors',
								isActive
									? 'bg-accent-badge text-accent-tint-fg border-accent-tint cursor-not-allowed'
									: disabled
										? cn(
												colors.classes.bgSecondary,
												colors.classes.borderDefault,
												colors.classes.textSecondary,
												'cursor-not-allowed',
											)
										: cn(
												colors.classes.bgPrimary,
												colors.classes.borderDefault,
												colors.classes.textPrimary,
												colors.classes.bgPrimaryHover,
											),
							)}
							type="button"
						>
							{page.toLocaleString()}
						</button>
					);
				})}

				{/* Next button */}
				<button
					onClick={() => {
						onPageChange(currentPage + 1);
					}}
					disabled={!hasNextPage || disabled}
					className={cn(
						'py-0.5 px-1.5 text-xs rounded-md border transition-colors',
						hasNextPage && !disabled
							? cn(
									colors.classes.bgPrimary,
									colors.classes.borderDefault,
									colors.classes.bgPrimaryHover,
								)
							: cn(
									colors.classes.bgSecondary,
									colors.classes.borderDefault,
									'cursor-not-allowed',
								),
					)}
					type="button"
					data-testid={
						testIdPrefix != null ? `${testIdPrefix}-pagination-next` : undefined
					}
				>
					<Icon
						icon={faChevronRight}
						size={12}
						color={
							hasNextPage && !disabled
								? colors.values.iconPrimary
								: colors.values.iconSecondary
						}
					/>
				</button>
			</div>
		</div>
	);
}
